import { useCallback, useEffect, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDPopup from "components/MDPopup";
import MDTypography from "components/MDTypography";

async function archiveRequest(path, method = "POST") {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${response.status})`);
    }
    return response.json();
}

function Row({ label, detail, actionLabel, disabled, onAction }) {
    return (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
            <MDBox>
                <MDTypography variant="button" fontWeight="medium">{label}</MDTypography>
                {detail && (
                    <MDTypography variant="caption" color="text" ml={1}>{detail}</MDTypography>
                )}
            </MDBox>
            <MDButton size="small" variant="outlined" color="info" disabled={disabled} onClick={onAction}>
                {actionLabel}
            </MDButton>
        </MDBox>
    );
}

// Parks and turbines to archive or restore. Archiving hides them everywhere and keeps their data.
function ArchivePanel({ parks, onChange }) {
    const [archives, setArchives] = useState({ parks: [], windmills: [] });
    const [pending, setPending] = useState(null);
    const [error, setError] = useState("");

    const loadArchives = useCallback(async () => {
        try {
            setArchives(await archiveRequest("/archives", "GET"));
            setError("");
        } catch (err) {
            setError(err.message);
        }
    }, []);

    useEffect(() => {
        loadArchives();
    }, [loadArchives]);

    const run = async (path) => {
        try {
            await archiveRequest(path);
            setError("");
            await loadArchives();
            onChange();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleConfirm = async () => {
        const item = pending;
        setPending(null);
        await run(item.path);
    };

    const activeTurbines = parks.flatMap((park) =>
        (park.windmills || []).map((windmill) => ({ ...windmill, parkName: park.name }))
    );

    return (
        <MDBox pt={6} pb={3}>
            <Grid container spacing={6}>
                <Grid item xs={12}>
                    <Card>
                        <MDBox
                            mx={2}
                            mt={-3}
                            py={1.5}
                            px={2}
                            variant="gradient"
                            bgColor="info"
                            borderRadius="lg"
                            coloredShadow="info"
                        >
                            <MDTypography variant="h6" color="white">Archives</MDTypography>
                        </MDBox>
                        <MDBox p={3}>
                            <MDTypography variant="caption" color="text" display="block" mb={2}>
                                An archived park or turbine disappears from the platform. Its data stays, and you can restore it here.
                            </MDTypography>
                            {error && (
                                <MDTypography variant="caption" color="error" display="block" mb={2}>{error}</MDTypography>
                            )}

                            <MDTypography variant="h6" mb={1}>Archived parks</MDTypography>
                            {archives.parks.length === 0 && (
                                <MDTypography variant="caption" color="text">None</MDTypography>
                            )}
                            {archives.parks.map((park) => (
                                <Row
                                    key={`park-${park.id}`}
                                    label={park.name}
                                    detail={park.address}
                                    actionLabel="Restore"
                                    onAction={() => run(`/parks/${park.id}/restore`)}
                                />
                            ))}

                            <MDTypography variant="h6" mt={3} mb={1}>Archived turbines</MDTypography>
                            {archives.windmills.length === 0 && (
                                <MDTypography variant="caption" color="text">None</MDTypography>
                            )}
                            {archives.windmills.map((windmill) => (
                                <Row
                                    key={`archived-turbine-${windmill.id}`}
                                    label={windmill.reference}
                                    detail={windmill.parkArchived ? `${windmill.parkName}: restore the park first` : windmill.parkName}
                                    actionLabel="Restore"
                                    disabled={windmill.parkArchived}
                                    onAction={() => run(`/windmills/${windmill.id}/restore`)}
                                />
                            ))}

                            <MDTypography variant="h6" mt={3} mb={1}>Active turbines</MDTypography>
                            {activeTurbines.length === 0 && (
                                <MDTypography variant="caption" color="text">None</MDTypography>
                            )}
                            {activeTurbines.map((windmill) => (
                                <Row
                                    key={`active-turbine-${windmill.id}`}
                                    label={windmill.reference}
                                    detail={windmill.parkName}
                                    actionLabel="Archive"
                                    onAction={() => setPending({
                                        path: `/windmills/${windmill.id}/archive`,
                                        name: `turbine ${windmill.reference}`,
                                    })}
                                />
                            ))}

                            <MDTypography variant="h6" mt={3} mb={1}>Active parks</MDTypography>
                            {parks.length === 0 && (
                                <MDTypography variant="caption" color="text">None</MDTypography>
                            )}
                            {parks.map((park) => (
                                <Row
                                    key={`active-park-${park.id}`}
                                    label={park.name}
                                    detail={park.address}
                                    actionLabel="Archive"
                                    onAction={() => setPending({
                                        path: `/parks/${park.id}/archive`,
                                        name: `park ${park.name} and all its turbines`,
                                    })}
                                />
                            ))}
                        </MDBox>
                    </Card>
                </Grid>
            </Grid>

            <MDPopup
                open={Boolean(pending)}
                onClose={() => setPending(null)}
                onConfirm={handleConfirm}
                item={pending}
                title="Confirm archive"
                body={(item) => (
                    <>
                        Archive the <strong>{item?.name}</strong>? It will disappear from the platform for all users. Its data stays, and you can restore it here.
                    </>
                )}
                confirmLabel="Archive"
                cancelLabel="Cancel"
            />
        </MDBox>
    );
}

export default ArchivePanel;
